import { tableSortLabelClasses } from '@mui/material'

import { assert } from '../../../../utils'

import { type ColumnView, ConnectwareError, ConnectwareErrorType, setColumnWidth } from '../../../../domain'

import { TABLE_SORT_LABEL_HOVER_FONT_WEIGHT } from '../../theme'
import { useAllColumnNames, useColumnNames, useColumns, useColumnView, useHasSelection } from './State'
import { columnMinWidth } from './Widths'

/** The widest a fit makes a column, so one long value doesn't push the other columns out of view */
const AUTO_FIT_MAX = 480

/** Out of sight and out of the page's flow, sized by its content alone, whatever the measured table's own layout */
const MEASURER_STYLE: Readonly<Partial<CSSStyleDeclaration>> = {
    position: 'absolute',
    top: '0',
    left: '0',
    visibility: 'hidden',
    pointerEvents: 'none',
    tableLayout: 'auto',
    width: 'max-content',
    minWidth: '0',
}

/** A sort label is bolder while hovered, so it's measured at that weight: the fitted width must still hold it whole when the user hovers the header */
const cloneCell = (cell: HTMLTableCellElement): HTMLTableCellElement => {
    const clone = cell.cloneNode(true)
    assert(clone instanceof HTMLTableCellElement, new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'A table cell must clone into a table cell'))
    clone.querySelectorAll<HTMLElement>(`.${tableSortLabelClasses.root}`).forEach(({ style }) => {
        style.fontWeight = String(TABLE_SORT_LABEL_HOVER_FONT_WEIGHT)
    })
    return clone
}

/**
 * The width each column's content takes on one line, header included: the widest of its cells.
 * The cells are copied into a hidden table beside the measured one, laid out automatically at `max-content`,
 * so every copied column is as wide as its widest content, and the measured table is left untouched.
 * Rows spanning several columns (the empty, error and loading rows) have no cell of a column's own, so they're left out
 */
export const measureColumnContentWidths = <Key>(
    table: HTMLTableElement,
    keys: readonly Key[],
    cellIndexOf: (key: Key) => number
): readonly (readonly [Key, number])[] => {
    const measurer = document.createElement('table')
    measurer.className = table.className
    Object.assign(measurer.style, MEASURER_STYLE)

    const rows = Array.from(table.rows)
        .filter((row) => Array.from(row.cells).every(({ colSpan }) => colSpan === 1))
        .map((source) => ({ source, copy: measurer.insertRow() }))

    const columns = keys.map((key): readonly [Key, readonly HTMLTableCellElement[]] => {
        const index = cellIndexOf(key)
        const cells = rows.map(({ source, copy }) => {
            const cell = source.cells.item(index)
            assert(cell, new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'A measured column must have a cell in every row', { index }))
            return copy.appendChild(cloneCell(cell))
        })
        return [key, cells]
    })

    table.after(measurer)

    try {
        return columns.map(([key, cells]) => [key, Math.max(0, ...cells.map((cell) => cell.getBoundingClientRect().width))])
    } finally {
        measurer.remove()
    }
}

/** A column's content width as its width, between its minimum and the fit's maximum; `null` when nothing was measured, as without a layout */
export const fitColumnWidth = (contentWidth: number, minWidth: number): number | null =>
    contentWidth > 0 ? Math.max(minWidth, Math.min(AUTO_FIT_MAX, Math.ceil(contentWidth))) : null

/**
 * Fits rendered columns to their content, from an element inside the table, as one column view change.
 * Columns that can't be measured keep their width, and with none measured nothing is committed
 */
export const useAutoFitColumns = (onChange: (view: ColumnView) => void): (from: Element, names: readonly string[]) => void => {
    const allNames = useAllColumnNames()
    const renderedNames = useColumnNames()
    const columns = useColumns()
    const view = useColumnView()
    const firstColumnCellIndex = Number(useHasSelection())

    return (from, names) => {
        const table = from.closest('table')
        assert(table, new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Columns can only be fitted from inside their table', { names }))

        const cellIndexOf = (name: string): number => {
            const index = renderedNames.indexOf(name)
            assert(index !== -1, new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Only a rendered column can be fitted', { name }))
            return firstColumnCellIndex + index
        }

        const fits = measureColumnContentWidths(table, names, cellIndexOf).flatMap(([name, contentWidth]) => {
            const column = columns[name]
            assert(column, new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'A fitted column must be configured', { name }))
            const minWidth = columnMinWidth(column)
            const width = fitColumnWidth(contentWidth, minWidth)
            return width === null ? [] : [{ name, width, minWidth }]
        })

        if (fits.length > 0) {
            onChange(fits.reduce((fitted, { name, width, minWidth }) => setColumnWidth(allNames, fitted, name, width, minWidth), view))
        }
    }
}
